import { cioFacetsToUrlFilters } from '~/grid/utils/cioUtils';
import { plusSizes } from '~/shared/constants/productSizes';

import { isCioColorFacet, isolateSizeFacets } from './filters';
import {
  buildImageServiceUrlFromItemNumber,
  buildSwatchImageUrlFromSku,
} from './images';

type FinalDetails = {
  promoCode: string;
  promoLabel: string;
  promoPrice: number;
  promoTypeId: string;
};

type DatasourceConfiguration = {
  sourceUrl: string;
  version: string;
};

type CustomerProductPrice = {
  final: number;
  finalDetails: FinalDetails;
  masterProductId: number;
  sales: number;
  vip: number;
  retail: number;
  outlet: number;
  pricingDatasource: DatasourceConfiguration;
};

type ProductImageData = {
  height: number;
  url: string;
  width: number;
};

type ProductImage = {
  angle: string;
  sizes: ProductImageData[];
};

type ProductImages = {
  default: ProductImage[];
  plus: ProductImage[];
};

type ActiveProduct = {
  id: number;
  masterProductId: number;
  label: string;
  permalink: string;
  imageServiceUrl: string;
  images: ProductImages | null;
  itemNumber: string;
  sku: string;
  pdpLink: string;
  pricing: CustomerProductPrice;
  swatchLabel: string;
  tagIdList: number[];
  membershipBrandId: string;
  productTypeId: number;
  // ===== below are for productPricing only =====
  tokenRedemptionQuantity: number;
  featuredProductLocationIdList: number[];
  productCategoryIdList: number[];
  activeFeaturedProductLocationCodeList: number[];
  activeFeaturedProductLocationIdList: string[];
  // isToken: boolean; // not currently available via Cio APIs
  // isTokenOnly: boolean; // not currently available via Cio APIs
  // bundleItems: any[]; // not currently available via Cio APIs
  // tokenRedemptionValue: number; // not currently available via Cio APIs
  // ===== below are for ProductCardCallout only =====
  dateExpected: string;
  availableQuantityMaster: number;
  // waitListAllowed: boolean; // not currently available via Cio APIs
  // skus: any[];
  // availableQuantity: number; // not currently available via Cio APIs
  // productIdObjectList: any[]; // not currently available via Cio APIs
  // ===== below are for tracking only =====
  reviewCount: number;
  defaultProductCategoryLabel: string;
  averageReview: number;
};

type Swatch = {
  productId: number;
  label: string;
  url: string;
  pdpLink: string;
  tagIdList?: number[];
};

/**
 * Generates a product detail page link based on the permalink and psrc.
 *
 * @param {Object} param - The parameters for generating the link.
 * @param {string} param.permalink - The permalink of the product.
 * @param {string} param.psrc - The psrc of the product.
 * @returns {string} - The generated product detail page link.
 */
const getPDPlink = ({
  permalink,
  psrc,
}: {
  permalink: string;
  psrc?: string;
}): string => {
  if (psrc) {
    const prefix = 'cio-';
    const psrcWithDedupedPrefix = `${prefix}${psrc?.replace(prefix, '')}`;
    return `/products/${permalink}?psrc=${psrcWithDedupedPrefix}`;
  }
  return `/products/${permalink}`;
};

/**
 * Data from Cio does not have a swatch label, so we need to get it from the color facet.
 * The assumption is that the first color facet option is the one we want to use and
 * matches the swatch label from related products.
 *
 * @param {Array} facets - The list of facets from the product data.
 * @returns {string} - The swatch label.
 */
const getSwatchLabelFromColorFacet = (facets: any[]): string => {
  const colorFacet = facets?.find((facet) => isCioColorFacet(facet?.name));

  // The product will typically have one (the first) color facet option associated with itself,
  // due to the nature of our product data, where we associate a separate product id for each color variation
  return colorFacet?.values?.[0];
};

/**
 * Formats the CIO product data to match the active product format.
 *
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @returns {ActiveProduct} - The formatted active product object.
 */
const formatCioProductToActiveProduct = ({
  cioProductData,
  productSource,
}: {
  cioProductData: any;
  productSource: string;
}): ActiveProduct => {
  return {
    id: Number(cioProductData?.id),
    images: null,
    imageServiceUrl: cioProductData?.image_service_url,
    itemNumber: cioProductData?.item_number,
    label: cioProductData?.label,
    masterProductId: Number(cioProductData?.id),
    membershipBrandId: cioProductData?.membership_brand_id,
    pdpLink: getPDPlink({
      permalink: cioProductData?.permalink,
      psrc: productSource,
    }),
    permalink: cioProductData?.permalink,
    pricing: cioProductData?.pricing,
    sku: cioProductData?.sku,
    swatchLabel: getSwatchLabelFromColorFacet(cioProductData?.facets),
    tagIdList:
      cioProductData?.tag_id_list?.map((tag: any) => Number(tag)) || [],
    tokenRedemptionQuantity: cioProductData?.token_redemption_quantity,
    featuredProductLocationIdList:
      cioProductData?.featured_product_location_id_list?.map((location: any) =>
        Number(location)
      ) || [],
    productCategoryIdList:
      cioProductData?.product_category_id_list?.map((category: any) =>
        Number(category)
      ) || [],
    activeFeaturedProductLocationCodeList:
      cioProductData?.active_featured_product_location_code_list?.map(
        (location: any) => String(location?.featured_product_location_code)
      ) || [],
    activeFeaturedProductLocationIdList:
      cioProductData?.active_featured_product_location_id_list?.map(
        (location: any) => Number(location?.featured_product_location_id)
      ) || [],
    productTypeId: cioProductData?.product_type_id,
    dateExpected: cioProductData?.date_expected,
    availableQuantityMaster: cioProductData?.available_quantity_master || 0,
    reviewCount: cioProductData?.review_count,
    defaultProductCategoryLabel: cioProductData?.default_product_category_label,
    averageReview: cioProductData?.average_review,
  };
};

/**
 * Formats the related product data to match the active product format.
 *
 * @param {Object} param.relatedProductData - The related product data.
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @returns {ActiveProduct} - The formatted active product object.
 */
const formatRelatedProductToActiveProduct = ({
  relatedProductData,
  cioProductData,
  productSource,
}: {
  relatedProductData: any;
  cioProductData: any;
  productSource: string;
}): ActiveProduct => {
  return {
    id: Number(relatedProductData?.masterProductId),
    images: relatedProductData?.images,
    imageServiceUrl:
      buildImageServiceUrlFromItemNumber(relatedProductData?.itemNumber) ||
      cioProductData?.image_service_url,
    itemNumber: relatedProductData?.itemNumber,
    label: cioProductData?.label,
    masterProductId: Number(relatedProductData?.masterProductId),
    membershipBrandId: relatedProductData?.membershipBrandId,
    pdpLink: getPDPlink({
      permalink: relatedProductData?.permalink,
      psrc: productSource,
    }),
    permalink: relatedProductData?.permalink,
    pricing: cioProductData?.pricing,
    sku: relatedProductData?.sku,
    swatchLabel: relatedProductData?.color,
    tagIdList:
      relatedProductData?.tags?.map((tag: { id: any }) => tag?.id) || [],
    tokenRedemptionQuantity: cioProductData?.token_redemption_quantity,
    featuredProductLocationIdList:
      cioProductData?.featured_product_location_id_list?.map((location: any) =>
        Number(location)
      ) || [],
    productCategoryIdList:
      cioProductData?.product_category_id_list?.map((category: any) =>
        Number(category)
      ) || [],
    activeFeaturedProductLocationCodeList:
      cioProductData?.active_featured_product_location_code_list?.map(
        (location: any) => String(location?.featured_product_location_code)
      ) || [],
    activeFeaturedProductLocationIdList:
      cioProductData?.active_featured_product_location_id_list?.map(
        (location: any) => Number(location?.featured_product_location_id)
      ) || [],
    productTypeId: cioProductData?.product_type_id,
    dateExpected: relatedProductData?.dateExpected,
    availableQuantityMaster: cioProductData?.available_quantity_master || 0,
    reviewCount: cioProductData?.review_count,
    defaultProductCategoryLabel: cioProductData?.default_product_category_label,
    averageReview: cioProductData?.average_review,
  };
};

/**
 * Get a unified formatted product object between CIO and GQL.
 *
 * @param {Array} param.relatedProducts - The list of related products.
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @param {number} param.activeProductId - The ID of the active product.
 * @param {Array} param.swatches - The list of swatches.
 * @returns {ActiveProduct} - The formatted product object.
 */
export const getActiveProduct = ({
  relatedProducts,
  cioProductData,
  productSource,
  activeProductId,
}: {
  relatedProducts: any[];
  swatches: any[];
  cioProductData: any;
  productSource: string;
  activeProductId: number;
}): ActiveProduct => {
  if (Number(activeProductId) === Number(cioProductData?.id)) {
    return formatCioProductToActiveProduct({
      cioProductData,
      productSource,
    });
  }

  const relatedProductData = relatedProducts?.find(
    (product) => Number(product.masterProductId) === Number(activeProductId)
  );

  return formatRelatedProductToActiveProduct({
    relatedProductData,
    cioProductData,
    productSource,
  });
};

/**
 * Formats the swatch data structure for a CIO product.
 *
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {string} param.productSource - The source of the product.
 * @param {Array} param.facets - The list of facets.
 * @returns {Swatch} - The formatted swatch object.
 */
const formatSwatchForCioProduct = ({
  cioProductData,
  productSource,
  facets,
}: {
  cioProductData: any;
  productSource: string;
  facets: any[];
}): Swatch => {
  return {
    productId: Number(cioProductData?.id),
    label: getSwatchLabelFromColorFacet(facets),
    url: buildSwatchImageUrlFromSku(cioProductData?.sku),
    pdpLink: getPDPlink({
      permalink: cioProductData?.permalink,
      psrc: productSource,
    }),
    tagIdList:
      cioProductData?.tag_id_list?.map((tag: any) => Number(tag)) || [],
  };
};

/**
 * Formats the swatch data structure for a related product from GQL.
 *
 * @param {Object} param.swatch - The swatch object.
 * @param {string} param.productSource - The source of the product.
 * @param {Object} param.relatedProduct - The related product object.
 * @returns {Swatch} - The formatted swatch object.
 */
const formatSwatchForRelatedProduct = ({
  swatch,
  productSource,
  relatedProduct,
}: {
  swatch: any;
  productSource: string;
  relatedProduct: any;
}): Swatch => {
  return {
    productId: swatch?.productId,
    label: swatch?.label,
    url: swatch?.url,
    pdpLink: getPDPlink({
      permalink: relatedProduct?.permalink,
      psrc: productSource,
    }),
    tagIdList: relatedProduct?.tags?.map((tag: any) => Number(tag)) || [],
  };
};
/**
 * Generates a list of swatches with unified swatch data structure with the primary swatch first.
 * The primary swatch is the one associated with the CIO product data.
 * The related product swatches are formatted and filtered to exclude the primary swatch.
 * Related products is not guaranteed to have isSelf swatch
 * we need to add it manually and make sure it's the first one
 *
 * @param {Array} param.swatches - The list of related product swatches.
 * @param {string} param.productSource - The source of the product.
 * @param {Object} param.cioProductData - The product data from CIO.
 * @param {Array} param.relatedProducts - The list of related products.
 * @returns {Array} - The list of swatches with the primary swatch first.
 */
export const getSwatchesWithPdpLink = ({
  swatches,
  productSource,
  cioProductData,
  relatedProducts,
}: {
  swatches: any[];
  productSource: string;
  cioProductData: any;
  relatedProducts: any[];
}): Swatch[] => {
  const primarySwatch = formatSwatchForCioProduct({
    cioProductData,
    productSource,
    facets: cioProductData?.facets,
  });

  if (!swatches?.length || !relatedProducts?.length) {
    return [primarySwatch];
  }

  const relatedProductSwatches = swatches
    ?.map((swatch) => {
      const relatedProduct = relatedProducts?.find(
        (product) =>
          Number(product.masterProductId) === Number(swatch?.productId)
      );

      // Remove swatches that are not available
      if (!relatedProduct?.isAvailable) {
        return null;
      }

      return formatSwatchForRelatedProduct({
        swatch,
        productSource,
        relatedProduct,
      });
    })
    ?.filter(Boolean);

  // filter out swatches that are the same as the primary swatch
  // there are some instances where related products have the same productId as the primary product
  // indicated by the `swatch.isSelf` property, but this is not always the case
  // so we need to filter out the swatches that have the same productId as the primary product
  // and manually build the primary swatch to have a consistent experience
  const filteredRelatedProductSwatches = relatedProductSwatches?.filter(
    (swatch) => swatch?.productId !== primarySwatch?.productId
  );

  // concatenate all swatches
  return [primarySwatch, ...filteredRelatedProductSwatches];
};

/**
 * Checks if the product should show a plus size image based on the user's profile
 * and selected URL filters if applicable for the product.
 * e.g. if the user selects only "size_top: 3XL" and the product is a pant,
 * then we should not show the plus size image
 *
 * @param {Object} param - The parameters for the function.
 * @param {Array} param.productFacets - The product facets.
 * @param {Object} param.selectedUrlFilters - The selected URL filters.
 * @param {boolean} param.isPlusProfile - Indicates if the user has a plus profile.
 * @returns {boolean} - True if the product should show a plus size image, false otherwise.
 */
export const shouldShowPlusSizeImageForProductCard = ({
  productFacets = [],
  selectedUrlFilters,
  isPlusProfile = false,
}: {
  productFacets: any[];
  selectedUrlFilters: Record<string, any>;
  isPlusProfile: boolean;
}): boolean => {
  // User has a plus profile, so we assume the preference is to see plus size images
  if (isPlusProfile) {
    return true;
  }

  if (!productFacets?.length || !selectedUrlFilters) {
    return false;
  }

  // Check if the user has selected any plus size filter options
  // and if those selections are relevant to the product type
  // e.g. if the user selects only "size_top: 3XL" and the product is a pant,
  // then we should not show the plus size image
  const { sizeFacets } = isolateSizeFacets(productFacets);
  const filteredSizeFacets = sizeFacets
    .filter((facet) => {
      if (facet.values?.length === 1 && facet.values[0] === 'no-size') {
        return false;
      }
      return true;
    })
    ?.map((facet) => cioFacetsToUrlFilters[facet.name] || facet.name);

  return filteredSizeFacets?.some((facet) => {
    const selectedFacet = selectedUrlFilters[facet];
    if (selectedFacet) {
      return selectedFacet.some((value) => plusSizes.has(value));
    }
    return false;
  });
};
